#!/usr/bin/env node

import os from 'node:os';

import semver from 'semver';

import { logger } from './src/console/logger.js';
import { LogLevel } from './src/console/logLevel.js';
import MultiBar from './src/console/multiBar.js';
import IgirException from './src/exceptions/igirException.js';
import Package from './src/globals/package.js';
import Igir from './src/igir.js';
import type Options from './src/models/options.js';
import ArgumentsParser from './src/modules/argumentsParser.js';
import EndOfLifeChecker from './src/modules/endOfLifeChecker.js';
import UpdateChecker from './src/modules/updateChecker.js';

// Double the number of frames tracked in a stack trace
// eslint-disable-next-line unicorn/no-nonstandard-builtin-properties
Error.stackTraceLimit = Math.max(Error.stackTraceLimit, 25);

logger.setLogLevel(LogLevel.WARN); // don't print any timestamps
logger.printHeader();

if (
  !process.versions.bun &&
  process.versions.node &&
  !semver.satisfies(process.versions.node, Package.ENGINES_NODE)
) {
  logger.error(`${Package.NAME} requires a Node.js version of ${Package.ENGINES_NODE}`);
  process.exit(1);
}

/**
 * Stop the global MultiBar if it is active, and print a newline after (such that more logs can be
 * neatly printed after this)
 */
function multiBarStopAndNewline(): void {
  const isNewlineNeeded = MultiBar.isActive();
  MultiBar.stop();
  if (isNewlineNeeded) {
    logger.newLine();
  }
}

process.once('SIGINT', () => {
  multiBarStopAndNewline();
  logger.notice(`Exiting ${Package.NAME} early`);
  process.exit(0);
});

// Parse CLI arguments
let options: Options;
try {
  const argv = process.argv.slice(2);
  options = new ArgumentsParser().parse(argv);

  logger.setLogLevel(options.getLogLevel());
  const debugLog = options.getDebugLog();
  if (debugLog !== undefined) {
    logger.notice(`Writing debug log to: ${debugLog}`);
    logger.openLogFile(debugLog);
  }

  const argvString = argv
    .map((arg) => {
      if (!arg.includes(' ')) {
        return arg;
      }
      return `"${arg.replaceAll('"', '\\"')}"`;
    })
    .join(' ');
  logger.trace(`Parsing CLI arguments: ${argvString}`);
  logger.trace(`Parsed CLI options: ${options.toString()}`);

  if (options.getHelp()) {
    process.exit(0);
  }
} catch (error) {
  // Explicitly do not log the stack trace, for readability
  logger.error(error);
  logger.newLine();
  process.exit(1);
}

if (options.getDebugLog()) {
  logger.trace(`process: ${process.platform} ${process.arch}`);
  logger.trace(`process.execPath: ${process.execPath}`);
  logger.trace(`process.versions: ${JSON.stringify(process.versions)}`);
  logger.trace(`os.version: ${os.version()}`);
  logger.trace(`os.freemem / os.totalmem: ${os.freemem()} / ${os.totalmem()}`);
  logger.trace(`os.userInfo: ${JSON.stringify(os.userInfo())}`);
  logger.trace(`os.cpus: ${JSON.stringify(os.cpus())}`);
  logger.trace(`os.availableParallelism: ${os.availableParallelism()}`);
  logger.trace(`package.json: ${JSON.stringify(Package.JSON)}`);
}

// Start the main process
try {
  new EndOfLifeChecker().check(process.version);
  void new UpdateChecker().check();

  await new Igir(options).main();
  MultiBar.stop();
} catch (error) {
  multiBarStopAndNewline();

  if (error instanceof IgirException) {
    logger.error(error);
  } else if (error instanceof Error && error.stack) {
    // Log the stack trace to help with bug reports
    logger.error(error.stack);
  } else {
    logger.error(error);
  }
  process.exit(1);
}
